import { apiCache } from './apiCache';

/**
 * Utility to manage and read shopping carts across the application.
 */
export const cartManager = {
  /**
   * Scans localStorage for all active carts and parses them.
   * Matches carts with branch data from the API cache.
   * @returns {Array} Array of active cart info objects.
   */
  getActiveCarts: () => {
    try {
      const activeCarts = [];
      const keys = Object.keys(localStorage);
      const branches = apiCache.getBranches() || [];

      keys.forEach(key => {
        if (key.startsWith('resto_cart_data_')) {
          try {
            const cartDataStr = localStorage.getItem(key);
            if (!cartDataStr) return;
            
            const parsedData = JSON.parse(cartDataStr);
            const cartItems = parsedData.items || parsedData; // Support both object format and raw array just in case
            if (!Array.isArray(cartItems) || cartItems.length === 0) return;

            // Extract store ID and order type from key
            // Format: resto_cart_data_{storeId}_{orderType}
            const parts = key.split('_');
            if (parts.length < 5) return; // resto, cart, data, storeId, orderType
            
            // Reconstruct if order type has underscores or handle standard case
            const storeIdStr = parts[3];
            const storeId = parseInt(storeIdStr, 10);
            const orderTypeStr = parts.slice(4).join('_');
            
            // Find branch to get name
            const branch = branches.find(b => b.id === storeId);
            
            activeCarts.push({
              storageKey: key,
              storeId: storeId,
              storeSlug: branch ? branch.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') : `store-${storeId}`,
              storeName: branch ? branch.name : `Store #${storeId}`,
              orderType: orderTypeStr,
              itemCount: cartItems.reduce((total, item) => total + (item.quantity || 1), 0),
              branchDetails: branch || null // Pass full branch for routing
            });
          } catch (e) {
            console.error(`Failed to load cart from key ${key}:`, e);
          }
        }
      });

      return activeCarts;
    } catch (error) {
      console.error('Error getting active carts:', error);
      return [];
    }
  },

  /**
   * Calculates the total number of items across all carts.
   */
  getTotalCartItems: () => {
    const carts = cartManager.getActiveCarts();
    return carts.reduce((total, cart) => total + cart.itemCount, 0);
  },

  /**
   * Clears all carts EXCEPT the one for the given store ID AND order type.
   * Called when a user navigates to a new store or changes order type to enforce single-store/single-type cart policy.
   * @param {number|string} currentStoreId - The store ID to keep.
   * @param {string} currentOrderType - The order type to keep (e.g., 'delivery' or 'collection').
   * @returns {number} Number of carts that were cleared.
   */
  clearOtherCarts: (currentStoreId, currentOrderType) => {
    try {
      const currentId = String(currentStoreId);
      const currentType = String(currentOrderType);
      const keys = Object.keys(localStorage);
      let clearedCount = 0;

      keys.forEach(key => {
        if (key.startsWith('resto_cart_data_')) {
          // Extract store ID and order type from key: resto_cart_data_{storeId}_{orderType}
          const parts = key.split('_');
          if (parts.length >= 5) {
            const storeIdStr = parts[3];
            const orderTypeStr = parts.slice(4).join('_');
            
            if (storeIdStr !== currentId || orderTypeStr !== currentType) {
              console.log(`🗑️ Clearing cart for store ${storeIdStr} & type ${orderTypeStr} (keeping: store ${currentId}, type ${currentType})`);
              localStorage.removeItem(key);
              clearedCount++;
            }
          }
        }
      });

      if (clearedCount > 0) {
        // Notify global cart indicator
        window.dispatchEvent(new Event('cart_updated'));
        console.log(`✅ Cleared ${clearedCount} other cart(s)`);
      }

      return clearedCount;
    } catch (error) {
      console.error('Error clearing other carts:', error);
      return 0;
    }
  }
};
